import React, { useState } from 'react';
import { Box, Typography, CircularProgress, IconButton, makeStyles, Tooltip, Dialog, DialogContent } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';

const useStyles = makeStyles((theme) => ({
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    borderRadius: 8,
    cursor: 'pointer',
  },
  imageWrapper: {
    position: 'relative',
    display: 'block',
    width: '100%',
    maxWidth: 260,
    maxHeight: 260,
    borderRadius: 8,
    overflow: 'hidden',
    lineHeight: 0,
  },
  imageDownloadBtn: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    backgroundColor: 'rgba(0,0,0,0.45)',
    color: '#fff',
    padding: 4,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.65)',
    },
  },
  // Lightbox
  lightboxOverlay: {
    backgroundColor: 'rgba(0,0,0,0.92)',
  },
  lightboxContent: {
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&:first-child': { paddingTop: 0 },
  },
  lightboxImg: {
    maxWidth: '100%',
    maxHeight: '90vh',
    objectFit: 'contain',
    display: 'block',
    borderRadius: 4,
  },
  lightboxClose: {
    position: 'fixed',
    top: 12,
    right: 12,
    color: '#fff',
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1,
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
  },
  lightboxDownload: {
    position: 'fixed',
    top: 12,
    right: 56,
    color: '#fff',
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1,
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
  },
  mediaContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    minHeight: 10,
    width: '100%',
  },
  mediaLoading: {
    backgroundColor: '#f0f0f0',
  },
  mediaError: {
    backgroundColor: '#ffebee',
    border: '1px dashed #f44336',
  },
  audioContainer: {
    width: '100%',
  },
  videoContainer: {
    minHeight: 160,
  },
  // WhatsApp-style file bubble
  fileBubble: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.06)',
    borderRadius: 8,
    padding: '10px 12px',
    width: '100%',
    boxSizing: 'border-box',
    gap: 10,
  },
  fileIcon: {
    backgroundColor: '#25d366',
    borderRadius: '50%',
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#fff',
  },
  fileInfo: {
    flex: 1,
    minWidth: 0, // required for text-overflow to work in flex child
    overflow: 'hidden',
  },
  fileName: {
    fontWeight: 500,
    fontSize: 13,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
  fileMeta: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    display: 'block',
  },
  downloadBtn: {
    flexShrink: 0,
    padding: 6,
  },
}));

function getFileExtension(mime, filename) {
  if (filename) {
    const parts = filename.split('.');
    if (parts.length > 1) return parts.pop().toUpperCase();
  }
  if (mime) {
    const parts = mime.split('/');
    return parts.pop().toUpperCase();
  }
  return 'FILE';
}

function DownloadButton({ url, filename, className, size = 'small' }) {
  if (!url) return null;
  return (
    <Tooltip title="Download">
      <IconButton
        className={className}
        size={size}
        component="a"
        href={url}
        download={filename || true}
        target="_blank"
        rel="noopener noreferrer"
      >
        <GetAppIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

export default function MediaMessage({ message }) {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [previewOpen, setPreviewOpen] = useState(false);

  const url = message?.content?.presignUrl;
  const mime = message?.content?.mediaData?.mimeType;
  const caption = message?.content?.mediaData?.caption || message?.content?.caption || '';
  const filename = message?.content?.mediaData?.fileName || message?.content?.mediaData?.filename || message?.content?.filename || '';
  const isImage = !!url && typeof mime === 'string' && mime.startsWith('image/');
  const isVideo = !!url && typeof mime === 'string' && mime.startsWith('video/');
  const isAudio = !!url && typeof mime === 'string' && mime.startsWith('audio/');
  const isFile = !!url && !isImage && !isVideo && !isAudio;
  const isLoading = message?.content?.isLoadingPresign;
  const hasError = message?.content?.presignError;

  const renderLoadingState = (type) => (
    <Box className={clsx(classes.mediaContainer, classes.mediaLoading)}>
      <Box textAlign="center">
        <CircularProgress size={24} />
        <Typography variant="caption" display="block" mt={1}>
          {t('Loading {{type}}...', { type })}
        </Typography>
      </Box>
    </Box>
  );

  const renderErrorState = (type) => (
    <Box className={clsx(classes.mediaContainer, classes.mediaError)}>
      <Typography variant="caption" color="error">
        {t('Failed to load {{type}}', { type })}
      </Typography>
    </Box>
  );

  const renderNotAvailableState = (type) => (
    <Box className={clsx(classes.mediaContainer, classes.mediaLoading)}>
      <Typography variant="caption" color="textSecondary">
        {t('{{type}} not available', { type })}
      </Typography>
    </Box>
  );

  // ── IMAGE ──────────────────────────────────────────────────────────────────
  if (isImage) {
    return (
      <>
        {url ? (
          <>
            {/* Thumbnail */}
            <Box
              className={classes.imageWrapper}
              onClick={() => setPreviewOpen(true)}
              style={{ cursor: 'pointer' }}
            >
              <Box
                component="img"
                src={url}
                alt={caption || 'Image'}
                className={classes.image}
                loading="lazy"
              />
              <DownloadButton url={url} filename={filename} className={classes.imageDownloadBtn} />
            </Box>

            {/* Lightbox */}
            <Dialog
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              maxWidth={false}
              PaperProps={{
                style: {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  margin: 16,
                  maxWidth: '95vw',
                  maxHeight: '95vh',
                },
              }}
              BackdropProps={{ className: classes.lightboxOverlay }}
            >
              <DialogContent className={classes.lightboxContent}>
                <Tooltip title="Close">
                  <IconButton className={classes.lightboxClose} size="small" onClick={() => setPreviewOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton
                    className={classes.lightboxDownload}
                    size="small"
                    component="a"
                    href={url}
                    download={filename || true}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GetAppIcon />
                  </IconButton>
                </Tooltip>
                <Box
                  component="img"
                  src={url}
                  alt={caption || 'Image'}
                  className={classes.lightboxImg}
                />
              </DialogContent>
            </Dialog>
          </>
        ) : isLoading ? (
          <Box className={clsx(classes.mediaContainer, classes.mediaLoading)}>
            <Box textAlign="center">
              <CircularProgress size={24} />
              <Typography variant="caption" display="block">Loading image...</Typography>
            </Box>
          </Box>
        ) : hasError ? (
          <Box className={clsx(classes.mediaContainer, classes.mediaError)}>
            <Typography variant="caption" color="error">Failed to load image</Typography>
          </Box>
        ) : (
          <Box className={clsx(classes.mediaContainer, classes.mediaLoading)}>
            <Typography variant="caption" color="textSecondary">Image not available</Typography>
          </Box>
        )}
        {caption && (
          <Typography variant="body2" style={{ marginTop: 4 }}>{caption}</Typography>
        )}
      </>
    );
  }

  // ── VIDEO ──────────────────────────────────────────────────────────────────
  // if (isVideo) {
  //   return (
  //     <>
  //       {url ? (
  //         <Box position="relative" display="inline-block">
  //           <video controls className={classes.image} src={url} />
  //           <DownloadButton url={url} filename={filename} className={classes.imageDownloadBtn} />
  //         </Box>
  //       ) : isLoading ? (
  //         <Box className={clsx(classes.mediaContainer, classes.mediaLoading, classes.videoContainer)}>
  //           <Box textAlign="center">
  //             <CircularProgress size={24} />
  //             <Typography variant="caption" display="block">Loading video...</Typography>
  //           </Box>
  //         </Box>
  //       ) : hasError ? (
  //         <Box className={clsx(classes.mediaContainer, classes.mediaError, classes.videoContainer)}>
  //           <Typography variant="caption" color="error">Failed to load video</Typography>
  //         </Box>
  //       ) : (
  //         <Box className={clsx(classes.mediaContainer, classes.mediaLoading, classes.videoContainer)}>
  //           <Typography variant="caption" color="textSecondary">Video not available</Typography>
  //         </Box>
  //       )}
  //       {caption && (
  //         <Typography variant="body2" style={{ marginTop: 4 }}>{caption}</Typography>
  //       )}
  //     </>
  //   );
  // }

  // ── AUDIO ──────────────────────────────────────────────────────────────────
  // if (isAudio) {
  //   return (
  //     <>
  //       {url ? (
  //         <Box>
  //           <audio controls className={classes.audioContainer} src={url} />
  //           <Box display="flex" justifyContent="flex-end" mt={0.5}>
  //             <DownloadButton url={url} filename={filename} />
  //           </Box>
  //         </Box>
  //       ) : isLoading ? (
  //         <Box display="flex" alignItems="center" justifyContent="center" bgcolor="#f0f0f0" p={2} borderRadius={8} className={classes.audioContainer}>
  //           <CircularProgress size={20} />
  //           <Typography variant="caption" style={{ marginLeft: 8 }}>Loading audio...</Typography>
  //         </Box>
  //       ) : hasError ? (
  //         <Box display="flex" alignItems="center" justifyContent="center" bgcolor="#ffebee" p={2} borderRadius={8} className={classes.audioContainer} border="1px dashed #f44336">
  //           <Typography variant="caption" color="error">Failed to load audio</Typography>
  //         </Box>
  //       ) : (
  //         <Box display="flex" alignItems="center" justifyContent="center" bgcolor="#f0f0f0" p={2} borderRadius={8} className={classes.audioContainer}>
  //           <Typography variant="caption" color="textSecondary">Audio not available</Typography>
  //         </Box>
  //       )}
  //       {caption && (
  //         <Typography variant="body2" style={{ marginTop: 4 }}>{caption}</Typography>
  //       )}
  //     </>
  //   );
  // }

  // ── GENERIC FILE (WhatsApp-style bubble) ───────────────────────────────────
  if (isFile && url) {
    const ext = getFileExtension(mime, filename);
    return (
      <>
        <Box className={classes.fileBubble}>
          {/* File icon */}
          <Box className={classes.fileIcon}>
            <AttachFileIcon fontSize="small" />
          </Box>

          {/* File info */}
          <Box className={classes.fileInfo}>
            {isLoading ? (
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                <CircularProgress size={14} />
                <Typography className={classes.fileMeta}>Loading...</Typography>
              </Box>
            ) : hasError ? (
              <Typography className={classes.fileMeta} style={{ color: '#f44336' }}>
                Failed to load file
              </Typography>
            ) : (
              <>
                <Typography className={classes.fileName}>
                  {filename || 'File'}
                </Typography>
                <Typography className={classes.fileMeta}>
                  {ext}
                </Typography>
              </>
            )}
          </Box>

          {/* Download button */}
          <DownloadButton url={url} filename={filename} className={classes.downloadBtn} />
        </Box>

        {caption && (
          <Typography variant="body2" style={{ marginTop: 4 }}>{caption}</Typography>
        )}
      </>
    );
  }

  // Unsupported / unknown message type
  return (
    <>
      <Box className={clsx(classes.mediaContainer)} style={{ padding: 8 }}>
        <Box textAlign="center">
          <Typography variant="caption" color="textSecondary">
            Unsupported message
          </Typography>
        </Box>
      </Box>
      {message?.content?.text && (
        <Typography variant="body2" style={{ marginTop: 4, color: 'rgba(0,0,0,0.6)' }}>
          {message.content.text}
        </Typography>
      )}
    </>
  );
}
